import * as React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Grid, Spacer, Typography } from '@emburse/ui-core';

import { StoreCompany } from '@Actions//company';
import { IAppStateContainer } from '@ReactStore/reducers';

import { CompanyApi } from '@ClientApi/company';
import { ICompanyModel } from '@Shared/models';

import { SignUpWorkflowFooter } from '../SignUpWorkflowFooter';
import { SignUpAmexPlans } from './SignUpAmexPlans';
import { SignUpSpendPlans } from './SignUpSpendPlans';

import * as style from './SignUpPlanSelection.styl';

interface ISignUpPlanSelectionProps {
  onPlanSelection: (navDirection: 'next' | 'back') => void;
  onPlanChange?: (billing_plan: ICompanyModel['billing_plan']) => void;
  onBack: ((navDirection: 'next' | 'back') => void) | null;
  onError: () => void;
  channel: string;
  showHeaders?: boolean;
  availablePlans?: string[];
  isMigration?: boolean;
}

export const SignUpPlanSelection: React.FC<ISignUpPlanSelectionProps> = ({
  onPlanSelection,
  onPlanChange,
  onBack,
  onError,
  channel,
  showHeaders = true,
  availablePlans,
  isMigration = false
}) => {
  const dispatch = useDispatch();
  const company = useSelector((state: IAppStateContainer) => state.company.company) as any;

  async function handleSelectPlan(billing_plan: ICompanyModel['billing_plan']) {
    try {
      const updatedCompany = await CompanyApi.updateCompanyBillingPlan(billing_plan);

      updatedCompany.utm_parameter = company.utm_parameter;
      dispatch(new StoreCompany(updatedCompany));
      onPlanChange?.(billing_plan);
      onPlanSelection('next');
    } catch (error) {
      onError();
    }
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ height: '100%', maxWidth: '754px' }} wrap="nowrap" className={style.global}>
      <Grid item container className="layout" direction="column">
        {showHeaders && (
          <Grid item>
            <Typography variant="h4" style={{ alignSelf: 'flex-start' }}>
              Select your plan
            </Typography>
            <Spacer size={8} />
          </Grid>
        )}

        {showHeaders && (
          <Grid item>
            <Typography variant="subtitle1">Choose the plan that works best for your company</Typography>
            <Spacer size={32} />
          </Grid>
        )}
        <Grid item container direction={{ xs: 'column', md: 'row' }} style={{ columnGap: 30, rowGap: 50 }} wrap="nowrap">
          {channel === 'amex' ? (
            <SignUpAmexPlans handleSelectPlan={handleSelectPlan} />
          ) : (
            <SignUpSpendPlans
              handleSelectPlan={handleSelectPlan}
              partnerId={company.partner_id}
              availablePlans={availablePlans}
              isMigration={isMigration}
            />
          )}
        </Grid>
      </Grid>
      <Grid item xs>
        <Spacer size={4} />
      </Grid>
      <Grid item container xs alignContent="flex-end">
        <SignUpWorkflowFooter onBack={onBack ? () => onBack('back') : undefined} showSupportLink={true} ariaLabel="plan-selection" />
      </Grid>
    </Grid>
  );
};
